import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ColDef, ICellRendererParams } from 'ag-grid-community';
import { DataItem } from '../../../../services/apiSLTP/activities';
import { capitalizeFirstLetter, formatName } from '../../../../utils/capitalize';
import { formatDate, formatDateToDDMMYYYY, formatDateToYYYYMMDD } from '../../../../utils/dates';
import { useDownloadCsvMutation } from '../../../../hooks/mutations/useDownloadMutation';
import { exportTypeEnum, permissionLevels } from '../../../../utils/enums';
import GenericAgGridTable from '../../GenericAgGridTable';
import ExportCsvIcon from '../../../ExportCsvIcon';
import { useAuth } from '../../../../hooks/useAuth';
import { stepModeEnum } from '../../../../constants';

interface TempWorkersTableProps {
    activities: DataItem[];
    currentDate: Date;
    stepMode: number;
}

const TempWorkersTable: React.FC<TempWorkersTableProps> = ({ activities, currentDate, stepMode }) => {
    const { t } = useTranslation();
    const { user } = useAuth();
    const { mutate: exportCsv, isLoading: isExportCsvLoading } = useDownloadCsvMutation();

    const rows = useMemo(() => {
        const dailyTempWorkers = activities
            .map((activity) =>
                activity.dailyTempWorkers.map((dtw) => ({
                    ...dtw,
                    activityId: activity._id,
                    siteName: activity.siteId.name,
                    reportUserFirstName: activity.reportId.userId.firstName,
                    reportUserLastName: activity.reportId.userId.lastName,
                    tempWorkerTypeDesignation: dtw.tempWorkerTypeId.designation,
                    tempWorkerTypeCodeProC: dtw.tempWorkerTypeId.codeProC,
                }))
            )
            .flat()
            .sort((a, b) => {
                if (a.tempWorkerTypeDesignation > b.tempWorkerTypeDesignation) return 1;
                if (a.tempWorkerTypeDesignation < b.tempWorkerTypeDesignation) return -1;
                if ((a.tempWorkerName || '') > (b.tempWorkerName || '')) return 1;
                if ((a.tempWorkerName || '') < (b.tempWorkerName || '')) return -1;
                return 0;
            });

        return dailyTempWorkers;
    }, [activities]);

    type TempWorkerRow = (typeof rows)[number];

    const columnDefs: ColDef<TempWorkerRow>[] = [
        {
            headerName: capitalizeFirstLetter(t('date')),
            colId: 'date',
            valueGetter: () => formatDateToDDMMYYYY(currentDate, '/'),
            initialHide: true,
        },
        {
            headerName: capitalizeFirstLetter(t('company')),
            field: 'tempWorkerTypeDesignation',
            colId: 'company',
            initialFlex: 1,
        },
        {
            headerName: capitalizeFirstLetter(t('codeProC')),
            field: 'tempWorkerTypeCodeProC',
            colId: 'codeProC',
        },
        {
            headerName: capitalizeFirstLetter(t('person')),
            field: 'tempWorkerName',
            colId: 'person',
        },
        {
            headerName: capitalizeFirstLetter(t('duration') + ' (' + t('hours') + ')'),
            field: 'durationHours',
            colId: 'duration',
        },
        {
            headerName: capitalizeFirstLetter(t('site')),
            field: 'siteName',
            colId: 'site',
        },
        {
            headerName: capitalizeFirstLetter(t('reportOf')),
            colId: 'reportOf',
            valueGetter: (params) => formatName(params.data?.reportUserFirstName, params.data?.reportUserLastName),
        },
        ...((user?.profileId.permsLevel === permissionLevels.ADMIN
            ? [
                  {
                      headerName: capitalizeFirstLetter(t('_id')),
                      field: '_id',
                      colId: '_id',
                      initialWidth: 250,
                      cellClass: 'usage-col-main',
                      headerClass: 'usage-col-main',
                  },
              ]
            : []) as ColDef<TempWorkerRow>[]),
    ];

    return (
        <>
            <GenericAgGridTable
                rowData={rows}
                columnDefs={columnDefs}
                exportCsvFileName={`supervision-tempworkers-${formatDate(currentDate)}`}
                headerRightComponent={
                    <>
                            <ExportCsvIcon
                                initDate={currentDate}
                                onExport={(startDate, endDate, fileName, delimiter) =>
                                    exportCsv({
                                        exportType: exportTypeEnum.TEMPWORKERS,
                                        queries: { startDate, endDate, delimiter, fileName },
                                    })
                                }
                                exportIsLoading={isExportCsvLoading}
                                subTitle={capitalizeFirstLetter(t('tempWorkers'))}
                            />
                   
                    </>
                }
                agGridProps={{ getRowId: ({ data }) => data._id }}
            />
        </>
    );
};

export default TempWorkersTable;
